import Vue from "vue"
import * as Sentry from "@sentry/vue"
import Axios from "axios"
import type { NuxtRuntimeConfig } from "@nuxt/types/config/runtime"
import { ErrorType } from "@evercam/api/types"

const ignoredErrors = [
  "Unexpected token '<'",
  'Unexpected token "<"',
  "Non-Error promise rejection",
  "timeout exceeded",
  "Unable to set property 'lastMousedownWasOutside' of undefined or null reference",
  'Unable to set property "lastMousedownWasOutside" of undefined or null reference',
  "Cannot set property ctrlKey of #<TouchEvent> which has only a getter",
  "AbortError: The play() request was interrupted because the media was removed from the document.",
  "AbortError: The play() request was interrupted because video-only background media was paused to save power.",
  "AbortError: The play() request was interrupted because the media was removed from the document. https://goo.gl/LdLk22",
  "AbortError: The play() request was interrupted by a call to pause(). https://goo.gl/LdLk22",
  "AbortError: The play() request was interrupted because video-only background media was paused to save power. https://goo.gl/LdLk22",
  "AbortError: The play() request was interrupted by a call to pause().",
  "AbortError: The operation was aborted.",
  "A network error occurred. This could be a CORS issue, a cancel request or a dropped internet connection.",
  "Request aborted",
  "Failed to fetch",
  "Snapshot not found in the selected timestamp", //This is the message we are displaying on requested snapshot not found on timeline
  "Network Error", //happens on bad internet connection
  "POST /users (400) BAD_ARGUMENT", // Triggered when a user try to signup using existing email
  "POST /auth/login (400) PASSWORD_RESET", // Triggered when logging in with a password after signing up via a provider; password reset required.
  "POST /auth/login (400) INVALID_CREDENTIALS", // Triggered when a user enters a wrong password or email
  "POST /auth/evercam (400) REQUIRE_SHARE_REQUEST", // Triggered when SSO signup lacks a share request
  "Maximum call stack size exceeded", // TODO: Temporarily ignored; will be removed after fixing the issue
]

export default () => (app: NuxtRuntimeConfig) => {
  const { router } = app.nuxt2Context.app
  const { isDev, $config } = app.nuxt2Context

  // Disable sentry in dev mode or preview link
  if (isDev || $config.public.appContext === "preview") {
    app.provide("errorTracker", {
      setUser: () => {},
      save: async () => new Promise<void>((res) => res()),
      saveAndNotify: async () => new Promise<void>((res) => res()),
      leaveBreadcrumb: () => {},
    })

    return
  }
  const processEvent = (event: Sentry.Event, hint: Sentry.EventHint) => {
    let breadcrumbs = event.breadcrumbs || []
    let breadcrumbsDataUrl = breadcrumbs?.[0]?.data?.url || ""
    const user = app.nuxt2Context.$auth?.getUserInfo()
    const firstEventException = event.exception?.values![0]

    // Ignore Axios response errors to prevent duplicate error reporting,
    // handling only those from the interceptor.
    const isAxiosError =
      hint?.originalException && Axios.isAxiosError(hint.originalException)

    const errorMessage = firstEventException?.value || ""
    const errorType = (firstEventException?.type || "") as ErrorType

    const isIgnoredErrorType = [
      ErrorType.CanceledError,
      ErrorType.UnhandledRejection,
      ErrorType.NotFoundError,
      ErrorType.GatewayTimeoutError,
      ErrorType.BadGatewayError,
    ]?.includes(errorType)

    const isIgnoredErrorTypeForNonLoggedUsers =
      !user.isLoggedIn &&
      [
        ErrorType.UnauthorizedError,
        ErrorType.ForbiddenError,
        ErrorType.ConflictError,
        ErrorType.InternalServerError,
      ].includes(errorType)

    const isExnvrError = /\/api\/(users\/login|devices)/.test(
      breadcrumbsDataUrl
    )

    const isIgnoredError = ignoredErrors.some((ignoredError) =>
      errorMessage.includes(ignoredError)
    )

    const ignoredErrorsWithSpecificType = [
      // Triggered when requestFullscreen() is called without user interaction (browser restriction).
      { error: "Permissions check failed", errorType: ErrorType.TypeError },
    ]

    const isIgnoredErrorWithSpecificType = ignoredErrorsWithSpecificType.some(
      (ignoredError) =>
        errorType === ignoredError.errorType &&
        errorMessage.includes(ignoredError.error)
    )

    const ignoredErrorsPatterns = [
      /^PATCH \/users\/[^/]+\/password \(400\) BAD_ARGUMENT$/, // Triggered when resetting password from the reset-password page
      /^PATCH \/users\/[^/]+\/password\/update \(400\) BAD_ARGUMENT$/, // Triggered when resetting password from the user account page
      /^GET \/cameras\/[^/]+\/recordings\/snapshots\/latest \(401\) INVALID_TOKEN$/, // Triggered for invalid token on latest snapshot request in recodings widget
      /^GET \/procore\/companies \(401\)$/, // Triggered for invalid token on procore companies request
      /^GET \/autodesk\/hubs \(401\)$/, // Triggered for invalid token on autodesk hubs
    ]

    const isMatchingIgnoredErrorPattern = ignoredErrorsPatterns.some(
      (pattern) => pattern.test(errorMessage)
    )

    const shouldIgnoreError =
      isIgnoredError ||
      isIgnoredErrorTypeForNonLoggedUsers ||
      isIgnoredErrorType ||
      isExnvrError ||
      isAxiosError ||
      isIgnoredErrorWithSpecificType ||
      isMatchingIgnoredErrorPattern

    if (shouldIgnoreError) {
      return null
    }

    if (Array.isArray(breadcrumbs)) {
      breadcrumbs = breadcrumbs.map((b) => {
        if (b?.category === "xhr" && typeof b.data?.url === "string") {
          b.data.url = b.data.url.replace(
            /(api_id|api_key|credentials|auth)/g,
            "_"
          )
        }

        return b
      })
    }

    return {
      ...event,
      contexts: {
        ...(event.contexts || {}),
        ...getExtraContext(hint?.originalException),
      },
      user,
      breadcrumbs,
    }
  }

  Sentry.init({
    Vue,
    dsn: $config.public.sentryDsn,
    tracingOptions: {
      trackComponents: true,
    },
    // @ts-expect-error - this is a valid option
    beforeSend: $config.isDev ? console.log : processEvent,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
      Sentry.browserProfilingIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 0.1,
  })
  app.provide("errorTracker", {
    setUser: Sentry.setUser,
    save: (
      error: unknown,
      feature: string | null = null,
      extraContext: Record<string, any> = {}
    ) => {
      if (!error) {
        return
      }
      let e = error
      if (!(e instanceof Error)) {
        try {
          e = JSON.stringify(e)
        } catch {
          e = error
        }
        e = new Error(String(e))
      }
      Sentry.captureException(e, (scope) => {
        scope.clear()
        if (feature) {
          scope.setTag("feature", feature)
        }

        const extraContextEntries = Object.entries(extraContext)
        if (extraContextEntries.length) {
          extraContextEntries.forEach(([key, value]) =>
            scope.setExtra(key, value)
          )
        }

        return scope
      })
    },
    async saveAndNotify(params: {
      ERROR: Error
      REQUEST_PAYLOAD: Record<any, any>
      FEATURE: string
    }) {
      const EVENT_ID = Sentry.captureException(params.ERROR, (scope) => {
        scope.clear()
        if (params.FEATURE) {
          scope.setTag("feature", params.FEATURE)
        }
        if (params.REQUEST_PAYLOAD) {
          scope.addAttachment({
            filename: "REQUEST_PAYLOAD.txt",
            data: JSON.stringify(params.REQUEST_PAYLOAD),
          })
        }

        return scope
      })
      if (isDev) {
        return
      }
      invokeCliqNotificationLambda(params.FEATURE, params.ERROR, EVENT_ID)
    },
    leaveBreadcrumb: (message: string | Record<string, any>) => {
      if (typeof message === "string") {
        Sentry.captureMessage(message)
      }

      if (typeof message === "object") {
        Sentry.addBreadcrumb(message)
      }
    },
  })
  async function invokeCliqNotificationLambda(
    feature: string,
    error: Error,
    eventId: string
  ) {
    const https = require("https")
    const message = `*${feature || "Unknown"} error* : \`${
      error?.message
    }\` [more details on Sentry.](https://sentry.io/organizations/evercam/issues/?query=${eventId})`

    return new Promise((resolve, reject) => {
      const req = https.request(
        {
          hostname: $config.public.cliqLambda,
          port: 443,
          path: "/",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            "Content-Length": message.length,
          },
        },
        (res) => {
          let responseChunks: Buffer[] = []
          res.on("data", (data: unknown) => {
            responseChunks.push(data as Buffer<ArrayBufferLike>)
          })
          res.on("end", () => {
            resolve(responseChunks)
          })
        }
      )

      req.on("error", (error: Error) => {
        reject(error)
      })
      req.write(message)
      req.end()
    })
  }
}

function getExtraContext(error?: unknown): any {
  if (Axios.isAxiosError(error)) {
    return { Axios: error }
  }

  return {}
}
